import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, FileText, Download, Search, Pill, Cpu, ExternalLink,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { listReferences, referenceFileUrl } from "../lib/api";
import { toast, Toaster } from "sonner";
import { useFeatureTimer } from "../lib/analytics";
import PdfViewer from "../components/PdfViewer";

const CATEGORIES = [
  { id: "all", label: "All", Icon: FileText },
  { id: "drug", label: "Drugs", Icon: Pill },
  { id: "device", label: "Devices", Icon: Cpu },
];

export default function References() {
  useFeatureTimer("references");
  const navigate = useNavigate();
  const [refs, setRefs] = useState([]);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    listReferences()
      .then(setRefs)
      .catch(() => toast.error("Failed to load references"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = refs
    .filter((r) => (category === "all" ? true : r.category === category))
    .filter((r) => {
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (
        r.title.toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q)
      );
    });

  return (
    <div className="min-h-screen bg-[#f1eee7] flex flex-col">
      <Toaster position="top-right" />

      <header className="border-b border-[#e4ded2] bg-[#fbf8f2]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate("/")}
            data-testid="refs-back-btn"
            className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#6b6258] hover:text-[#6f2a30] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
          <div className="h-5 w-px bg-[#e4ded2]" />
          <div className="flex items-center gap-2.5">
            <img src="/project-rn-logo-horizontal.png" alt="Project RN" className="h-8 w-auto" />
            <div className="h-5 w-px bg-[#e4ded2]" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6b6258]">
                Reference Library
              </div>
              <div className="font-semibold tracking-tight text-[#1a1612]">
                Quick reference sheets
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto px-6 py-8">
        {/* Search + category filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-[#6b6258] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search sheets…"
              data-testid="refs-search"
              className="w-full pl-9 pr-3 py-2 bg-[#fbf8f2] border border-[#e4ded2] rounded-sm text-sm focus:outline-none focus:border-[#6f2a30]"
            />
          </div>
          <div className="flex items-center border border-[#e4ded2] bg-[#fbf8f2] rounded-sm overflow-hidden">
            {CATEGORIES.map((c) => {
              const Icon = c.Icon;
              const active = category === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`refs-cat-${c.id}`}
                  className={`flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest font-medium transition-colors ${
                    active
                      ? "bg-[#6f2a30] text-[#f1eee7]"
                      : "text-[#6b6258] hover:bg-[#f1eee7]"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#6b6258]">
            {category === "all" ? "Library" : category === "drug" ? "Drugs" : "Devices"}
          </div>
          <span className="font-mono text-xs text-[#6b6258]" data-testid="refs-count">
            {filtered.length} {filtered.length === 1 ? "sheet" : "sheets"}
          </span>
        </div>

        {/* List with inline expand */}
        <div className="space-y-2">
          {loading && (
            <div className="text-[#6b6258] text-sm font-mono">loading…</div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="bg-[#fbf8f2] border border-dashed border-[#e4ded2] rounded-sm p-8 text-center">
              <FileText className="h-8 w-8 text-[#9c8f7e] mx-auto mb-3" strokeWidth={1.4} />
              <div className="text-sm text-[#6b6258]">No sheets found</div>
              {query && (
                <button
                  onClick={() => setQuery("")}
                  data-testid="refs-clear-search"
                  className="text-xs text-[#6f2a30] underline mt-2"
                >
                  Clear search
                </button>
              )}
            </div>
          )}
          {filtered.map((r) => {
            const isOpen = openId === r.id;
            return (
              <div
                key={r.id}
                data-testid={`ref-${r.id}`}
                className={`bg-[#fbf8f2] border rounded-sm transition-colors ${
                  isOpen ? "border-[#6f2a30]" : "border-[#e4ded2] hover:border-[#c8bfa9]"
                }`}
              >
                <button
                  onClick={() => setOpenId(isOpen ? null : r.id)}
                  data-testid={`ref-toggle-${r.id}`}
                  className="w-full text-left px-4 py-3 group"
                >
                  <div className="flex items-center gap-3">
                    <div className="shrink-0">
                      {r.category === "drug" ? (
                        <Pill className="h-4 w-4 text-[#6f2a30]" strokeWidth={1.6} />
                      ) : (
                        <Cpu className="h-4 w-4 text-[#6f2a30]" strokeWidth={1.6} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className={`font-semibold text-sm tracking-tight truncate transition-colors ${
                          isOpen ? "text-[#6f2a30]" : "text-[#1a1612] group-hover:text-[#6f2a30]"
                        }`}>{r.title}</div>
                      </div>
                      {r.description && !isOpen && (
                        <p className="text-xs text-[#6b6258] mt-0.5 leading-relaxed line-clamp-1">
                          {r.description}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 text-[#9c8f7e]">
                      {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-[#e4ded2]" data-testid={`ref-preview-${r.id}`}>
                    <div className="flex items-center justify-end gap-2 px-4 py-2 bg-[#f1eee7]">
                      <a
                        href={referenceFileUrl(r.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`ref-open-${r.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-[#d8ceb8] text-[#1a1612] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#fbf8f2] transition-colors"
                      >
                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.8} />
                        Open in new tab
                      </a>
                      <a
                        href={referenceFileUrl(r.id)}
                        download={r.original_filename}
                        data-testid={`ref-download-${r.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-[#6f2a30] text-[#f1eee7] text-xs uppercase tracking-widest font-medium rounded-sm hover:bg-[#582227] transition-colors"
                      >
                        <Download className="h-3.5 w-3.5" strokeWidth={1.8} />
                        Download
                      </a>
                    </div>
                    <div className="bg-white" style={{ height: "60vh" }}>
                      <div className="h-full flex flex-col">
                        <PdfViewer
                          key={r.id}
                          fileUrl={referenceFileUrl(r.id)}
                          filename={r.original_filename}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-[10px] uppercase tracking-widest text-[#9c8f7e] mt-6 leading-relaxed">
          Read-only view. Only the administrator can add, edit, or reorder sheets.
        </p>
      </main>
    </div>
  );
}
